import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Eye,
  X,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Sparkles,
  Video,
  Image,
} from 'lucide-react';
import type { QuickPreview } from '@/types/video-enhancer';

interface QuickPreviewPanelProps {
  quickPreview: QuickPreview;
  onClose: () => void;
  onRender: () => void;
}

export const QuickPreviewPanel: React.FC<QuickPreviewPanelProps> = ({
  quickPreview,
  onClose,
  onRender,
}) => {
  const [currentClip, setCurrentClip] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleClose = () => {
    if (videoRef.current) videoRef.current.pause();
    setIsPlaying(false);
    onClose();
  };

  const jumpToClip = (index: number) => {
    setCurrentClip(index);
    if (videoRef.current) {
      const clip = quickPreview.clips[index];
      if (clip) {
        videoRef.current.currentTime = clip.inTime;
        if (isPlaying) {
          videoRef.current.play().catch(console.error);
        }
      }
    }
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current || !isPlaying) return;
    const clip = quickPreview.clips[currentClip];
    if (clip && videoRef.current.currentTime >= clip.outTime) {
      if (currentClip < quickPreview.clips.length - 1) {
        jumpToClip(currentClip + 1);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
        setCurrentClip(0);
      }
    }
  };

  return (
    <div className="panel mt-4 animate-in slide-in-from-top-2 duration-300">
      {/* Header */}
      <div className="panel-header flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider">
          <Eye className="h-3.5 w-3.5 text-primary" />
          Timeline Preview
        </h3>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="h-6 w-6 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="p-4">
        {quickPreview.clips.length > 0 ? (
          <div className="space-y-4">
            {/* Video Player */}
            <div className="relative aspect-video max-h-[400px] overflow-hidden rounded-lg bg-[hsl(var(--timeline))] ring-1 ring-border">
              {quickPreview.clips[currentClip]?.type === 'video' ? (
                <video
                  ref={videoRef}
                  className="h-full w-full object-contain"
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={() => {
                    const clip = quickPreview.clips[currentClip];
                    if (videoRef.current && clip) {
                      videoRef.current.currentTime = clip.inTime;
                      if (isPlaying) {
                        videoRef.current.play().catch(console.error);
                      }
                    }
                  }}
                  onCanPlay={() => {
                    if (isPlaying && videoRef.current?.paused) {
                      videoRef.current.play().catch(console.error);
                    }
                  }}
                  src={quickPreview.clips[currentClip]?.url}
                />
              ) : quickPreview.clips[currentClip]?.type === 'image' ? (
                <img
                  src={quickPreview.clips[currentClip]?.url}
                  alt="Preview"
                  className="h-full w-full object-contain"
                />
              ) : null}

              {/* Overlay badges */}
              <div className="absolute left-3 top-3 rounded bg-black/80 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm">
                Clip {currentClip + 1}/{quickPreview.clips.length}
              </div>
              <div className="absolute bottom-3 left-3 rounded bg-black/80 px-2 py-1 text-[10px] text-white/80 backdrop-blur-sm">
                {quickPreview.clips[currentClip]?.name}
              </div>
            </div>

            {/* Playback Controls */}
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                disabled={currentClip === 0}
                onClick={() => jumpToClip(Math.max(0, currentClip - 1))}
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
              >
                <SkipBack className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                onClick={() => {
                  if (isPlaying) {
                    videoRef.current?.pause();
                    setIsPlaying(false);
                  } else {
                    videoRef.current?.play().catch(console.error);
                    setIsPlaying(true);
                  }
                }}
                className="h-12 w-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                disabled={currentClip === quickPreview.clips.length - 1}
                onClick={() => jumpToClip(Math.min(quickPreview.clips.length - 1, currentClip + 1))}
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
              >
                <SkipForward className="h-4 w-4" />
              </Button>
            </div>

            {/* Clip Thumbnails */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium text-muted-foreground">
                  Clips ({quickPreview.clips.length})
                </h4>
                <span className="text-xs text-muted-foreground">
                  Total: {quickPreview.summary.totalDuration}
                </span>
              </div>
              <ScrollArea className="w-full">
                <div className="flex gap-1.5 pb-2">
                  {quickPreview.clips.map((clip, index) => (
                    <button
                      key={clip.id}
                      onClick={() => jumpToClip(index)}
                      className={`group relative flex-shrink-0 w-20 overflow-hidden rounded transition-all ${
                        index === currentClip
                          ? 'ring-2 ring-primary ring-offset-1 ring-offset-background'
                          : 'ring-1 ring-border hover:ring-muted-foreground'
                      }`}
                    >
                      <div className="aspect-video bg-[hsl(var(--timeline))]">
                        {clip.thumbnailUrl ? (
                          <img src={clip.thumbnailUrl} alt={clip.name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            {clip.type === 'video' ? (
                              <Video className="h-4 w-4 text-primary" />
                            ) : (
                              <Image className="h-4 w-4 text-success" />
                            )}
                          </div>
                        )}
                        <div className="absolute left-0.5 top-0.5 rounded bg-black/70 px-1 text-[9px] font-medium text-white">
                          {index + 1}
                        </div>
                        <div className="absolute bottom-0.5 right-0.5 rounded bg-black/70 px-1 text-[9px] font-mono text-white/80">
                          {clip.duration.toFixed(1)}s
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 border-t border-border pt-4">
              <Button variant="ghost" onClick={handleClose} className="text-muted-foreground">
                Close
              </Button>
              <Button
                onClick={() => {
                  if (videoRef.current) videoRef.current.pause();
                  setIsPlaying(false);
                  onRender();
                }}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Quick Render
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <p className="text-sm text-muted-foreground">No clips to preview.</p>
          </div>
        )}
      </div>
    </div>
  );
};
